import {
  MedicineStructureVo,
  PharmacyHandlingMedicineList,
  PharmacyHandlingMedicineState,
} from '@/@types/medicine';
import {
  GetPharmacyHandlingMedicine,
  GetPharmacyHandlingMedicines,
  GetPharmacyTreatmentMedicineStructures,
  RegisterPharmacyHandlingMedicinesVariables
} from '@/api/bff_types';
import { MEDICINE_PAGE_LIMIT } from '@/hooks/medicine/useMedicineListViewModel';

// 약품 단일 조회
export const parsePharmacyHandlingMedicineForEdit = (
  data?: GetPharmacyHandlingMedicine
): PharmacyHandlingMedicineState => {
  if (!data) return {} as PharmacyHandlingMedicineState;
  return {
    treatmentOption: {
      value: data.pharmacyHandlingMedicine.treatmentId,
      label: data.pharmacyHandlingMedicine.treatmentName
    },
    medicineOption: {
      value: data.pharmacyHandlingMedicine.medicineId,
      label: data.pharmacyHandlingMedicine.medicineName
    },
    medicineStandardPrices:
      data.pharmacyHandlingMedicine.medicineStandardPrices.map(item => ({
        option: {
          value: item.medicineStandardId,
          label: item.medicineStandard
        },
        price: item.price
      }))
  };
};

// 약품 목록
export const parsePharmacyHandlingMedicinesVo = (
  data?: GetPharmacyHandlingMedicines
): PharmacyHandlingMedicineList => {
  if (!data) return {} as PharmacyHandlingMedicineList;
  return data.pharmacyHandlingMedicines;
};

// 약품 목록 테이블 데이터
export const parsePharmacyHandlingMedicinesForTable = (
  data?: GetPharmacyHandlingMedicines
): PharmacyHandlingMedicineList => {
  if (!data) return {} as PharmacyHandlingMedicineList;
  const parsedData = parsePharmacyHandlingMedicinesVo(data);
  const column = Array.from(
    { length: MEDICINE_PAGE_LIMIT },
    () => new Object()
  );
  const tableData = Object.assign(column, parsedData.pharmacyHandlingMedicines);
  return {
    totalCount: data.pharmacyHandlingMedicines.totalCount,
    totalPageCount: data.pharmacyHandlingMedicines.totalPageCount,
    pharmacyHandlingMedicines: tableData
  };
};

interface GetPharmacyMedicineOptionsProps {
  isEdit?: boolean;
  medicineStructures: MedicineStructureVo;
  selectedMedicine: PharmacyHandlingMedicineState;
}

// 약품 선택 select options
export const getPharmacyMedicineOptions = ({
  medicineStructures,
  selectedMedicine
}: GetPharmacyMedicineOptionsProps) => {
  if (!selectedMedicine.treatmentOption) return [];

  const {
    treatmentOption: { value: treatmentId }
  } = selectedMedicine;

  return medicineStructures.treatmentMedicines?.filter(
    treatment => treatment.treatmentId === treatmentId
  )[0].pharmacyMedicineOptions;
};

// 약품 규격 선택 select options
export const getPharmacyMedicineStandardOptions = ({
  isEdit,
  medicineStructures,
  selectedMedicine
}: GetPharmacyMedicineOptionsProps) => {
  if (!selectedMedicine.medicineOption) return [];

  const {
    treatmentOption: { value: treatmentId },
    medicineOption: { value: medicineId }
  } = selectedMedicine;

  const options = medicineStructures.treatmentMedicines
    ?.filter(treatment => treatment.treatmentId === treatmentId)[0]
    .pharmacyMedicines.filter(
      medicine => medicine.medicineId === medicineId
    )[0].pharmacyMedicineStandardOptions;

  const formattedOptions = options?.map(option => {
    return {
      ...option,
      disabled: !isEdit && option.disabled
        ? true
        : selectedMedicine.medicineStandardPrices?.some(
            price => option.value === price.option.value
          )
    };
  });

  return formattedOptions;
};

// 취급 가능 증상 약품 구조
export const parseMedicineStructureVo = (
  data?: GetPharmacyTreatmentMedicineStructures
): MedicineStructureVo => {
  if (!data) return {} as MedicineStructureVo;
  return {
    treatmentMedicines:
      data.pharmacyTreatmentMedicineStructures.treatmentMedicines.map(
        treatmentMedicine => {
          return {
            ...treatmentMedicine,
            pharmacyMedicines: treatmentMedicine.pharmacyMedicines.map(
              pharmacyMedicine => {
                return {
                  ...pharmacyMedicine,
                  pharmacyMedicineStandards:
                    pharmacyMedicine.pharmacyMedicineStandards.map(
                      medicineStandard => {
                        return {
                          ...medicineStandard
                        };
                      }
                    ),
                  pharmacyMedicineStandardOptions:
                    pharmacyMedicine.pharmacyMedicineStandards.map(
                      pharmacyMedicineStandard => ({
                        value: pharmacyMedicineStandard.medicineStandardId,
                        label: pharmacyMedicineStandard.medicineStandard,
                        disabled: !pharmacyMedicineStandard.isActivated
                      })
                    )
                };
              }
            ),
            pharmacyMedicineOptions: treatmentMedicine.pharmacyMedicines.map(
              pharmacyMedicine => ({
                value: pharmacyMedicine.medicineId,
                label: pharmacyMedicine.medicineName,
                disabled: pharmacyMedicine.pharmacyMedicineStandards.every(
                  standard => !standard.isActivated
                )
              })
            )
          };
        }
      ),
    treatmentMedicineOptions:
      data.pharmacyTreatmentMedicineStructures.treatmentMedicines.map(
        treatmentMedicine => ({
          value: treatmentMedicine.treatmentId,
          label: treatmentMedicine.treatmentName
        })
      )
  };
};

// 약품 등록/수정
export const parsePharmacyHandlingMedicinesDto = (
  data: PharmacyHandlingMedicineState
): RegisterPharmacyHandlingMedicinesVariables => {
  return {
    request: {
      medicineId: data.medicineOption.value as string,
      medicineStandardPrices: data.medicineStandardPrices.map(item => {
        return {
          medicineStandardId: item.option.value as string,
          price: item.price
        };
      })
    }
  };
};
